/**
 * @module @bq-core/data
 * @version 1.0.0
 * @file A library to query Block Qualified contracts.
 * @copyright Ethereum Foundation 2022
 * @license MIT
 * @see [Github]{@link undefined}
*/
import axios from 'axios';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Check if the parameter type is correct.
 * @param value Parameter value.
 * @param name Parameter name.
 * @param type Expected parameter type.
 */
function checkParameter(value, name, type) {
    if (typeof value !== type) {
        throw new TypeError(`Parameter '${name}' is not ${type === "object" ? "an" : "a"} ${type}`);
    }
}

/**
 * Returns the subgraph URL related to the network passed as a parameter.
 * @param network Semaphore supported network.
 * @returns Subgraph URL.
 */
function getURL(network) {
    switch (network) {
        case "maticmum":
            return `https://api.thegraph.com/subgraphs/name/0xdeenz/blockqualified`;
        default:
            throw new TypeError(`Network '${network}' is not supported`);
    }
}

/**
 * Returns the response data of an HTTP request.
 * @param url HTTP URL.
 * @param config Axios request configuration.
 * @returns Request data.
 */
/* istanbul ignore next */
function request(url, config) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios(url, Object.assign({ headers: Object.assign({ "Content-Type": "application/json" }, config === null || config === void 0 ? void 0 : config.headers) }, config));
        return data === null || data === void 0 ? void 0 : data.data;
    });
}

class BlockQualifiedSubgraph {
    /**
     * Initializes the subgraph object with one of the supported networks or a custom URL.
     * @param networkOrSubgraphURL Supported Semaphore network or custom Subgraph URL.
     */
    constructor(networkOrSubgraphURL = "maticmum") {
        checkParameter(networkOrSubgraphURL, "networkOrSubgraphURL", "string");
        if (networkOrSubgraphURL.startsWith("http")) {
            this._url = networkOrSubgraphURL;
            return;
        }
        this._url = getURL(networkOrSubgraphURL);
    }
    /**
     * Returns the subgraph URL.
     * @returns Subgraph URL.
     */
    get url() {
        return this._url;
    }
    /**
     * Returns a specific group.
     * @param groupId Group id.
     * @param group Options to select the group parameters.
     * @returns Specific group.
     */
    getGroupMembers(credentialId, group) {
        return __awaiter(this, void 0, void 0, function* () {
            checkParameter(credentialId, "credentialId", "number");
            let groupId;
            switch (group) {
                case "grade":
                    groupId = (3 * (credentialId - 1) + 1).toString();
                    break;
                case "credentials":
                    groupId = (3 * (credentialId - 1) + 2).toString();
                    break;
                case "no-credentials":
                    groupId = (3 * (credentialId - 1) + 3).toString();
                    break;
                default:
                    throw new TypeError(`Group '${group}' is not valid`);
            }
            const config = {
                method: "post",
                data: JSON.stringify({
                    query: `{
                    groups(where: { id: "${groupId}" }) {
                        id
                        merkleTree {
                            root
                            depth
                            numberOfLeaves
                        }
                        members(orderBy: index) {
                            identityCommitment
                        }
                    }
                }`
                })
            };
            const { groups } = yield request(this._url, config);
            return groups[0].members.map((member) => member.identityCommitment);
        });
    }
}

export { BlockQualifiedSubgraph };
